import assert from 'assert';
import Backbone from 'backbone';

import BenchlingConfig from 'BenchlingConfig';
import {Part} from 'BioJS';
import {Sequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {ProteinPart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkHighlightRegion,
  LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR,
  LINK_HIGHLIGHT_COLOR,
  ViewBiointelligentLinkData,
} from 'molbio/maps/BiointelligentLinksConstants';
import {isNucleotideSequence} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import {transcriptionId} from 'molbio/maps/linear/TranscriptionHelpers';
import {TranscriptionMetadata} from 'molbio/TranscriptionsUtil';

export interface ViewPartData extends ViewBiointelligentLinkData {
  sequenceId: string;
}

interface BasePartHighlightRegion extends BiointelligentLinkHighlightRegion {
  // name and isArchived are only used for rendering old-style non-overlapping parts with the text
  // on top.
  name: string;
  isArchived: boolean;
  partRefSequenceId: string | null;
  // outOfSync represents whether the link data (e.g. residues) is no longer in sync with the container sequence.
  outOfSync: boolean;
}

interface SinglePartHighlightRegion extends BasePartHighlightRegion {
  // partRefSequenceId is only needed to show the bioentity popover on part labels for non-overlapping parts,
  // but can be removed once PARTS_ENABLE_OVERLAPPING_PARTS flag is removed.
  partRefSequenceId: string;
}
interface OverlappingPartHighlightRegion extends BasePartHighlightRegion {
  partRefSequenceId: null;
}
export type PartHighlightRegion = SinglePartHighlightRegion | OverlappingPartHighlightRegion;

interface LinkPoint {
  index: number;
  isEnd: boolean;
  linkRef: ViewBiointelligentLinkData;
}

// A ViewBiointelligentLink is a "fake" part just for the purposes of presenting the correct interface to methods in
// RingView that expect a backbone model, and are too complicated to completely refactor at this time.
interface ViewBiointelligentLinkAttributes extends BasePartHighlightRegion {
  annularLayer?: 0;
  forceLabel?: boolean;
  hasCircularLabel?: boolean;
}

export interface ViewBiointelligentLink extends Backbone.Model {
  get<K extends keyof ViewBiointelligentLinkAttributes>(key: K): ViewBiointelligentLinkAttributes[K];
  set(obj: Partial<ViewBiointelligentLinkAttributes>, options?: {silent?: boolean}): ViewBiointelligentLink;
  set<K extends keyof ViewBiointelligentLinkAttributes>(
    key: K,
    value: ViewBiointelligentLinkAttributes[K]
  ): ViewBiointelligentLink;
}

export class ViewBiointelligentLink extends Backbone.Model {
  start(): number {
    return this.get('start');
  }

  end(): number {
    return this.get('end');
  }

  color(): string {
    return this.get('outOfSync') ? LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR : LINK_HIGHLIGHT_COLOR;
  }

  hover(): boolean {
    return false;
  }

  selected(): boolean {
    return false;
  }

  displayLabel(): string {
    return this.get('name');
  }

  uid(): string {
    return this.cid;
  }
}

function adjustEnd(end: number, seqLength: number): number {
  return end === 0 ? seqLength : end;
}

function isCrossOrigin(link: ViewBiointelligentLinkData): boolean {
  return link.end !== 0 && link.start >= link.end;
}

function linearizeLinks(
  links: Array<ViewBiointelligentLinkData>
): Array<{start: number; end: number; link: ViewBiointelligentLinkData}> {
  return links.flatMap((l) =>
    isCrossOrigin(l)
      ? [
          {start: l.start, end: 0, link: l},
          {
            start: 0,
            end: l.end,
            link: l,
          },
        ]
      : [{start: l.start, end: l.end, link: l}]
  );
}

function pointCompare(point1: LinkPoint, point2: LinkPoint): number {
  const endComp =
    point1.linkRef.id === point2.linkRef.id
      ? Number(point1.isEnd) - Number(point2.isEnd)
      : Number(point2.isEnd) - Number(point1.isEnd);
  return point1.index === point2.index ? endComp : point1.index - point2.index;
}

function translateLinksToHighlightRegionsHelper<TLinkRegion extends BiointelligentLinkHighlightRegion>(
  links: Array<ViewBiointelligentLinkData>,
  containerSequenceLength: number,
  makeLinkRegion: (
    point: LinkPoint,
    prevPoint: LinkPoint,
    curLinks: {[id: string]: ViewBiointelligentLinkData}
  ) => TLinkRegion
): Array<TLinkRegion> {
  if (!links.length) {
    return [];
  }
  const highlightRegions: Array<TLinkRegion> = [];
  const points: Array<LinkPoint> = linearizeLinks(links).flatMap((l) => [
    {index: l.start, isEnd: false, linkRef: l.link},
    {index: adjustEnd(l.end, containerSequenceLength), isEnd: true, linkRef: l.link},
  ]);
  points.sort((p1, p2) => pointCompare(p1, p2));
  const curLinks: {[id: string]: ViewBiointelligentLinkData} = {[points[0].linkRef.id]: points[0].linkRef};
  for (const [prevIndex, point] of points.slice(1).entries()) {
    const prevPoint = points[prevIndex];
    const nlinks = Object.values(curLinks).length;
    if (point.index > prevPoint.index && nlinks > 0) {
      highlightRegions.push(makeLinkRegion(point, prevPoint, curLinks));
    }
    // Avoids deleting link from curLinks if region represents a cross-origin link that starts and ends at the
    // same position, fixing bug where latter region of a cross-origin link with the same start and end
    // position was unhighlighted.
    if (point.isEnd && !(prevPoint.index === point.index && prevPoint.linkRef === point.linkRef)) {
      delete curLinks[point.linkRef.id];
    } else {
      curLinks[point.linkRef.id] = point.linkRef;
    }
  }

  return highlightRegions;
}

/**
 * Derives the strand (forward, reverse, or both) that should be highlighted for a set of links.
 *
 * This is the union of all highlights for this region. For example, if we have a forward transcription and
 * a reverse transcription in this region, we should highlight both strands.
 */
function deriveHighlightedStrand(links: Array<ViewBiointelligentLinkData>): Strand {
  // This should be guaranteed by the if statement in translateLinksToHighlightRegionsHelper.
  assert(links.length > 0, 'Expected at least one link in links array.');
  const hasForwardStrandHighlight = links.some((l) => l.highlightedStrand === 1);
  const hasReverseStrandHighlight = links.some((l) => l.highlightedStrand === -1);
  const hasBothStrandsHighlight = links.some((l) => l.highlightedStrand === 0);
  if ((hasForwardStrandHighlight && hasReverseStrandHighlight) || hasBothStrandsHighlight) {
    return 0;
  }
  return hasForwardStrandHighlight ? 1 : -1;
}

/**
 * This function translates a set of biointelligent links, which may or may not overlap, into a set of smaller
 * regions that do not overlap. Each region represents a section of the sequence where one or more links exist.
 * This way we can consistently highlight regions in the sequence that are covered by one or more links.
 * For example, given these 4 links:
 *
 * 0      7     13   18   23      31  35      44
 * {------l1---------}    {----l3-----}
 *        {-l2--}                 {-----l4-----}
 * GATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACA
 *
 * it derives 6 regions:
 *
 * {-r1--}{-r2-}{-r3-}    {-r4---}{r5}{-r6-----}
 * GATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACA
 *
 * The algorithm works like this:
 *  - It splits links that cross the origin into two links that do not cross the origin.
 *  - Then, it sorts all the start/end points for the links. E.g. [0s, 7s, 13e, 18e, 23s, 31s, 35e, 44e].
 *  - Finally, it iterates through the points, and makes regions between consecutive points.
 *
 * We could change this algorithm to merge instead of split now that we don't color overlapping regions
 * differently. But that may not be much simpler, and it's a riskier change from the previous state.
 *
 * @param links
 * @param containerSequenceLength
 */
export function translateLinksToHighlightRegions(
  links: Array<ViewBiointelligentLinkData>,
  containerSequenceLength: number
): Array<BiointelligentLinkHighlightRegion> {
  function translateLinkToHighlightRegion(
    point: LinkPoint,
    prevPoint: LinkPoint,
    curLinks: {[id: string]: ViewBiointelligentLinkData}
  ): BiointelligentLinkHighlightRegion {
    return {
      start: prevPoint.index,
      end: point.index === containerSequenceLength ? 0 : point.index,
      links: Object.values(curLinks),
      highlightedStrand: deriveHighlightedStrand(Object.values(curLinks)),
    };
  }

  return translateLinksToHighlightRegionsHelper(
    links,
    containerSequenceLength,
    translateLinkToHighlightRegion
  );
}

/**
 * Adaptation of translateLinksToHighlightRegions to accomodate old-style non overlapping parts.
 */
export function splitViewPartsIntoHighlightRegions(
  parts: Array<ViewPartData>,
  containerSequenceLength: number
): Array<PartHighlightRegion> {
  function translatePartToHighlightRegion(
    point: LinkPoint,
    prevPoint: LinkPoint,
    curLinks: {[id: string]: ViewPartData}
  ): PartHighlightRegion {
    const isOverlap = Object.values(curLinks).length > 1;
    return {
      start: prevPoint.index,
      end: point.index === containerSequenceLength ? 0 : point.index,
      // name and isArchived are only used for rendering old-style non-overlapping parts with the text
      // on top.
      name: point.linkRef.name,
      isArchived: point.linkRef.isArchived,
      outOfSync: Object.values(curLinks).some((p) => p.outOfSync),
      partRefSequenceId: isOverlap ? null : Object.values(curLinks)[0].sequenceId,
      links: Object.values(curLinks),
      // Old style part links always highlight both strands.
      highlightedStrand: 0,
    };
  }
  return translateLinksToHighlightRegionsHelper(
    parts,
    containerSequenceLength,
    translatePartToHighlightRegion
  );
}

/**
 * Only for use with old-style non-overlapping parts.
 */
export function splitPartsModelsIntoHighlightRegions(
  parts: Array<Part>,
  containerSequence: Sequence
): Array<PartHighlightRegion> {
  const viewPartsData = parts.map(
    (p) =>
      ({
        id: p.id != null ? p.id.toString() : p.cid,
        sequenceId: p.sequence().id,
        name: p.name(),
        isArchived: p.sequence().isArchived(),
        start: p.start(),
        end: p.end(),
        outOfSync: !p.isInSync(containerSequence._sequence()),
        linkType: BiointelligentLinkType.DNA_PART,
        // We always highlight both strands for DNA parts.
        highlightedStrand: 0,
      }) satisfies ViewPartData
  );
  return splitViewPartsIntoHighlightRegions(viewPartsData, containerSequence.length());
}

/**
 * Handles determining highlight regions for all nucleotide features (overlapping parts, transcriptions, translation links).
 */
export function splitNucleotideFeatureModelsIntoHighlightRegions(
  parts: Array<Part>,
  transcriptions: Array<TranscriptionMetadata>,
  containerSequence: Sequence
): Array<BiointelligentLinkHighlightRegion> {
  const viewPartsData: Array<ViewBiointelligentLinkData> = parts.map((p) => ({
    id: p.id != null ? p.id.toString() : p.cid,
    sequenceId: p.sequence().id,
    name: p.name(),
    isArchived: p.sequence().isArchived(),
    start: p.start(),
    end: p.end(),
    outOfSync: !p.isInSync(containerSequence._sequence()),
    linkType: BiointelligentLinkType.DNA_PART,
    // We always highlight both strands for DNA parts.
    highlightedStrand: 0,
  }));
  const viewTranscriptionsData: Array<ViewBiointelligentLinkData> = transcriptions.map((t) => ({
    id: transcriptionId(t),
    name: t.rnaSequence.name,
    isArchived: false,
    start: t.start,
    end: t.end,
    outOfSync: false,
    linkType: BiointelligentLinkType.TRANSCRIPTION,
    // Transcriptions only highlight the forward or reverse strand of a DNA sequence.
    highlightedStrand: t.isReverseComplement ? -1 : 1,
  }));
  return translateLinksToHighlightRegions(
    [...viewPartsData, ...viewTranscriptionsData],
    containerSequence.length()
  );
}

export function splitAaSequencePartModelsIntoHighlightRegions(
  parts: Array<ProteinPart>,
  aaSequenceLength: number
): Array<PartHighlightRegion> {
  const viewPartsData = parts.map(
    (p) =>
      ({
        id: p.id.toString(),
        sequenceId: p.proteinId,
        // name and isArchived are not needed for proteins since AA parts cannot exist without overlapping parts
        name: '',
        isArchived: false,
        start: p.start,
        end: p.end,
        outOfSync: false,
        linkType: BiointelligentLinkType.AMINO_ACID_PART,
        // The notion of "strands" doesn't apply for protein parts.
        highlightedStrand: 0,
      }) satisfies ViewPartData
  );
  return splitViewPartsIntoHighlightRegions(viewPartsData, aaSequenceLength);
}

export function getBiointelligentLinkRegionsForSlice(
  start: number,
  end: number,
  polymer: Sequence | AaSequence,
  linkTypes: Array<BiointelligentLinkType>
): Array<BiointelligentLinkHighlightRegion> {
  const polymerLength = polymer.length();
  // Start can extend past the end of the sequence in cases where we have extra slices; return an empty array
  // in those cases, and clamp the end such that start/end is always in the [0, seqLength] range.
  if (start > polymerLength) {
    return [];
  }
  const clampedEnd = Math.min(end, polymerLength);
  if (isNucleotideSequence(polymer)) {
    const shouldShowParts =
      linkTypes.includes(BiointelligentLinkType.RNA_PART) ||
      linkTypes.includes(BiointelligentLinkType.DNA_PART);
    const parts = shouldShowParts ? polymer.parts().getSequencePartsBetween(start, clampedEnd) : [];
    if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      return splitPartsModelsIntoHighlightRegions(parts, polymer);
    }

    const transcriptions = linkTypes.includes(BiointelligentLinkType.TRANSCRIPTION)
      ? polymer.getTranscriptionsBetween(start, clampedEnd)
      : [];
    return splitNucleotideFeatureModelsIntoHighlightRegions(parts, transcriptions, polymer);
  } else {
    return splitAaSequencePartModelsIntoHighlightRegions(
      polymer.getPartsBetween(start, clampedEnd),
      polymer.length()
    );
  }
}
